import React, { useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { Formik, Form as FormikForm, useFormikContext } from 'formik';
import isEqual from 'lodash/isEqual';

import { PreventLocationChange } from 'pages/Dashboard/Common/Utils';
import { InviteType } from 'constants/enums';

import { HorizontalStepper } from '.';

export interface PageStep {
  // Name of the step that will appear in the steps list on the left (make sure to translate it!)
  name: string;
  // React component to be displayed for this step
  page: any;
  // Is the list of steps on the left hidden when this step is active?
  hideStepper?: boolean;
  // Does this step appear in the list of steps on the left, or is it hidden?
  // If this is set to true, the StepperPage component will still process this step
  hideStep?: boolean;
  // Is the footer at the bottom hidden for this step?
  hideFooter?: boolean;
  // Do we show a warning message if the user attempts to switch tab while this step is active?
  preventLocationChange?: boolean;
  // Do we skip this step?
  skipStep?: boolean;
  // Names of the fields to check in case we need to show the optional skip button
  fieldNames?: string[];
  // validation done when clicking next to go to the next step, must be async to permit async server checks
  validateStep?: (values: any) => Promise<boolean>;
}

export interface FooterControls {
  onPrev: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  activeStepIndex: number;
}

interface IStepperPageProps {
  buildOrderedSteps: (changeStep: (formik: any, stepIndex: number) => void) => PageStep[];
  defaultValues: any;
  autoValidate?: boolean;
  handleValidation: (formik: any, stepIndex: number) => void;
  submitAction: (values: any) => void;
  skipStep?: (values: any, stepIndex: number, orderedSteps: PageStep[]) => boolean;
  switchStep?: (values: any) => void;
  onProceedPrompt?: any;
  activeInviteType?: InviteType;
  stepperClassNames?: string;
  constrainToModal?: boolean;
  renderFooter: (formik: any, controls: FooterControls) => React.ReactNode;
  contentPaddingBottom?: number;
  outerStyle?: React.CSSProperties;
}

/**
 * This component is a page wrapper to create a single page with multiple steps like the patient invitation page
 *
 * The steps will be displayed on the left of the screen (1/4 of the width) while the active step's page is displayed
 * on the right (3/4 of the width)
 *
 * @param buildOrderedSteps
 * This is a function used to build the steps that will be displayed by this component.
 * It had to be done using a function since some steps like the patient invitation confirmation's
 * step navigates back to the 1st step if the user clicks a button.
 * To accomodate this use case, the StepperPage "changeStep" function that modifies the active step
 * is passed in parameter when calling this function and can be used optionally.
 *
 * @param defaultValues
 * Default formik values
 *
 * @param autoValidate
 * If true, the form will be validated on mount and on every change/blur. Defaults to true.
 *
 * @param handleValidation
 * Perform any formik validation necessary for the current step.
 *
 * @param submitAction
 * Perform this submit action when the last step is complete
 *
 * @param switchStep (optional)
 * Perform an action when the step is changed
 *
 */
const StepValidator = ({ activeStepIndex }: { activeStepIndex: number }) => {
  const { validateForm } = useFormikContext();
  useEffect(() => {
    validateForm();
  }, [activeStepIndex, validateForm]);
  return null;
};

const StepperPage = ({
  buildOrderedSteps,
  defaultValues,
  autoValidate = true,
  handleValidation,
  submitAction,
  switchStep,
  skipStep,
  onProceedPrompt,
  activeInviteType,
  stepperClassNames = '',
  constrainToModal = false,
  renderFooter,
  contentPaddingBottom,
  outerStyle
}: IStepperPageProps) => {
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);

  /*
   * Start every step at its own top.
   *
   * The element below that scrolls is one persistent node: React swaps the step's children into it
   * and leaves `scrollTop` alone, so arriving from a long step lands you partway down the next one.
   * On the order wizard that hid the cost summary, which sits at the top of Review, and it read as
   * the prices being missing entirely rather than merely out of view (Charles, then Tatiana,
   * 2026-09-15).
   */
  const stepContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    stepContentRef.current?.scrollTo({ top: 0 });
  }, [activeStepIndex]);

  /**
   * Change the active step and reset formik to prevent error messages from instantly appearing
   */
  const changeStep = (formik: any, stepIndex: number) => {
    formik.setSubmitting(true);
    formik.setErrors({});
    formik.setTouched({});
    if (orderedSteps[stepIndex].skipStep) {
      changeStep(formik, stepIndex - 1);
    } else {
      setActiveStepIndex(stepIndex);
    }
  };

  // List of all the steps, ordered by index
  const orderedSteps: PageStep[] = buildOrderedSteps(changeStep);
  const activeStep = orderedSteps[activeStepIndex];
  const isLastStep = activeStepIndex === orderedSteps.length - 1;

  /**
   * Execute the submit action if the user reached the last step
   * Otherwise, go to the next step, notify the step change with switchStep and
   * reset formik to prevent error messages from instantly appearing
   */
  const handleSubmit = async (values: any, formik: any, stepIndex: number) => {
    if (isLastStep) {
      submitAction(values);
    } else if (skipStep && skipStep(values, stepIndex + 1, orderedSteps)) {
      handleSubmit(values, formik, stepIndex + 1);
    } else {
      let valid = true;
      if (activeStep.validateStep) {
        valid = await activeStep.validateStep(values);
      }
      if (valid) {
        switchStep && switchStep(values);
        formik.setTouched({});
        setActiveStepIndex(stepIndex + 1);
      }
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={defaultValues}
      validate={(values) => handleValidation(values, activeStepIndex)}
      validateOnChange={autoValidate}
      validateOnBlur={autoValidate}
      validateOnMount={autoValidate}
      onSubmit={(values, formik) => handleSubmit(values, formik, activeStepIndex)}
      className={stepperClassNames}
    >
      {(formik) => {
        const visibleSteps = orderedSteps.filter((step) => !step.hideStep && !step.skipStep);
        const visibleStepIndex = visibleSteps.findIndex((s) => s.name === activeStep.name);

        return (
          <>
            <StepValidator activeStepIndex={activeStepIndex} />
            {activeStep.preventLocationChange && (formik.dirty || activeStepIndex !== 0) && (
              <PreventLocationChange
                messageKey="sessionProgressLostKey"
                confirmKey="leaveKey"
                cancelKey="stayKey"
                messageTitleKey="confirmQuitKey"
                onProceed={onProceedPrompt}
              />
            )}
            <div
              className={!constrainToModal ? 'h-offset-topNav overflow-hidden p-3' : undefined}
              style={!constrainToModal ? { ...outerStyle } : undefined}
            >
              <div
                className={!constrainToModal ? 'd-flex flex-column h-100' : undefined}
                style={
                  !constrainToModal
                    ? { border: '0.5px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }
                    : undefined
                }
              >
                <FormikForm
                  autoComplete="off"
                  noValidate={activeInviteType !== InviteType.BULK_ORDER_DEVICES}
                  className={
                    !constrainToModal ? 'd-flex flex-column flex-grow-1 overflow-hidden' : undefined
                  }
                >
                  {!activeStep.hideStepper && (
                    <div
                      className="d-flex justify-content-center px-4 py-3 flex-shrink-0"
                      style={{
                        backgroundColor: '#F5F6F8',
                        borderBottom: '1px solid #E5E7EB'
                      }}
                    >
                      <HorizontalStepper
                        steps={visibleSteps.map((step) => step.name)}
                        currentStepIndex={visibleStepIndex}
                      />
                    </div>
                  )}
                  <div
                    ref={stepContentRef}
                    className={`bg-white overflow-x-hidden overflow-y-auto p-4 ${!constrainToModal ? 'flex-grow-1' : ''}`}
                    style={{
                      marginBottom: contentPaddingBottom ?? 14,
                      ...(constrainToModal ? { maxHeight: '60vh' } : {})
                    }}
                  >
                    {activeStep.page}
                  </div>
                  {!activeStep.hideFooter &&
                    renderFooter(formik, {
                      onPrev: () => changeStep(formik, activeStepIndex - 1),
                      isFirstStep: activeStepIndex === 0,
                      isLastStep,
                      activeStepIndex
                    })}
                </FormikForm>
              </div>
            </div>
          </>
        );
      }}
    </Formik>
  );
};

export default StepperPage;
